// @flow

import React, { Fragment } from 'react';

import { Spacer } from '@zapier/design-system';
import { useMachine } from '@xstate/react';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import useTrackPath from 'app/developer-v3/components/PartnerSupportForm/hooks/useTrackPath';
import { AutoScroll } from 'app/developer-v3/components/PartnerSupportForm/components/AutoScroll';
import { useFormContext } from 'app/developer-v3/components/PartnerSupportForm/contexts/FormContext';
import {
  CommunityStep,
  HelpBlock,
  Interstitial,
  Separator,
  Upgrade,
} from 'app/developer-v3/teams/team-technical-support-operations/get-help';

import Contact from './Contact';
import Docs from './Docs';
import Submitted from './Submitted';
import helpMachine from './machine';
import { PartnerAdditionalHelp } from '../Topic/AdditionalHelp/PartnerAdditionalHelp';

import type { ComponentProps } from './types';

const Help = ({
  actions: { unconfirm, answer, setMeta, submit },
  community,
  docs,
  pathId,
  canContinue,
  contact,
  data,
  state,
}: ComponentProps) => {
  const trackPath = useTrackPath();
  const {
    size,
    zap,
    isEmailSupportEligible,
    isPremierSupportEligible,
  } = useFormContext();

  const [
    {
      context: {
        hasDocs,
        hasCommunity,
        dotIndex,
        dotTooltips,
        currentStep: { key, title, hasFeedback },
      },
    },
    send,
  ] = useMachine(helpMachine, {
    context: {
      hasDocs: !!docs,
      hasCommunity: !!community,
      hasContact: !!contact,
      isEmailSupportEligible,
      unconfirm,
      trackPath: path => trackPath(pathId, path),
    },
  });

  /*
    Scroll to the top of the content whenever:
    * The user goes to a different step in the help flow
    * The contact form has been successfully submitted
  */
  const scrollKey = `${key} ${state === 'submitted' ? 'true' : 'false'}`;

  const helpStep = children => (
    <AutoScroll scrollKey={scrollKey}>
      <HelpBlock
        currentStepIndex={dotIndex}
        dotTooltips={dotTooltips}
        onGoBack={() => send('PREV_STEP')}
        onGoForward={() => send('NEXT_STEP')}
        onGoToStep={i => send('STEP_FROM_DOT', { dotIndex: i })}
        showFeedback={hasFeedback}
        size={size}
        title={title}
      >
        {children}
      </HelpBlock>
    </AutoScroll>
  );

  switch (key) {
    case 'interstitial':
      return (
        <AutoScroll scrollKey={scrollKey}>
          <Interstitial
            hasArticles={hasDocs}
            hasCommunityConversations={hasCommunity}
            isEmailSupportEligible={isEmailSupportEligible}
            isPremierSupportEligible={isPremierSupportEligible}
            onClickBack={() => send('PREV_STEP')}
            onClickNext={() => send('NEXT_STEP')}
            onClickSkip={() => send('CONTACT')}
            size={size}
          />
        </AutoScroll>
      );

    case 'docs':
      return helpStep(<Docs {...docs} size={size} zap={zap} />);

    case 'community':
      return helpStep(<CommunityStep links={community} size={size} />);

    case 'upgrade':
      return helpStep(
        <Upgrade
          onClickUpgrade={() => send('PREV_STEP')}
          viewPlansUrl={new URL('/pricing', ZAPIER_APP_BASE_URL).href}
        />
      );

    case 'contact':
      if (state === 'submitted') {
        return (
          <Fragment>
            {helpStep(<Submitted size={size} />)}
            <Spacer height={60} />
            <Separator />
            <PartnerAdditionalHelp size={size} />
          </Fragment>
        );
      }

      return helpStep(
        <Contact
          answer={answer}
          canContinue={canContinue}
          data={data}
          isSubmitting={state === 'submitting'}
          items={contact}
          setMeta={setMeta}
          submit={submit}
        />
      );

    default:
      return null;
  }
};

export default Help;
